<template>
  <section id="connected-apps" class="scroll-mt-20" aria-labelledby="connected-apps-heading">
    <h2 id="connected-apps-heading" class="text-xl font-semibold text-gray-900 dark:text-white">
      Connected apps
    </h2>
    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
      AI assistants and other apps you allowed to use ManageRunners through the MCP connector.
    </p>

    <div
      class="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
    >
      <div v-if="loading" class="p-6 text-sm text-gray-500 dark:text-gray-400" role="status">
        Loading connected apps…
      </div>

      <div v-else-if="listError" class="p-6" role="alert">
        <p class="text-sm text-red-600 dark:text-red-400">{{ listError }}</p>
        <button
          type="button"
          class="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
          @click="loadApps"
        >
          Try again
        </button>
      </div>

      <div v-else-if="apps.length === 0" class="p-6 text-center">
        <LinkIcon class="mx-auto size-10 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No connected apps</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Apps appear here after you allow them access, for example when you add ManageRunners as a
          connector in Claude.
        </p>
      </div>

      <ul
        v-else
        role="list"
        class="divide-y divide-gray-100 dark:divide-white/10"
        aria-label="Connected apps"
      >
        <li
          v-for="app in apps"
          :key="app.grantId"
          class="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:px-6"
          :data-grant-id="app.grantId"
        >
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {{ appName(app) }}
            </p>
            <p
              v-if="app.clientKind === 'METADATA_DOCUMENT'"
              class="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400 break-all"
            >
              {{ hostOf(app.clientId) }}
            </p>
            <ul
              role="list"
              class="mt-2 flex flex-wrap gap-1.5"
              :aria-label="`Permissions of ${appName(app)}`"
            >
              <li
                v-for="scope in app.scopes"
                :key="scope"
                class="inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-xs inset-ring"
                :class="
                  scopeInfo(scope)?.mutation
                    ? 'bg-amber-50 text-amber-800 inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20'
                    : 'bg-gray-50 text-gray-600 inset-ring-gray-500/10 dark:bg-white/5 dark:text-gray-300 dark:inset-ring-white/10'
                "
                :title="scopeInfo(scope)?.description"
              >
                {{ scope }}
              </li>
            </ul>
            <dl
              class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400"
            >
              <div class="flex gap-1">
                <dt>Connected</dt>
                <dd>
                  <time :datetime="app.createdAt">{{ formatDate(app.createdAt) }}</time>
                </dd>
              </div>
              <div class="flex gap-1">
                <dt>Last used</dt>
                <dd>
                  <time v-if="app.lastUsedAt" :datetime="app.lastUsedAt">{{
                    formatDate(app.lastUsedAt)
                  }}</time>
                  <template v-else>Never</template>
                </dd>
              </div>
              <div class="flex gap-1">
                <dt>Expires if unused</dt>
                <dd>
                  <time :datetime="app.expiresAt">{{ formatDate(app.expiresAt) }}</time>
                </dd>
              </div>
            </dl>
          </div>
          <button
            type="button"
            class="self-start rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/10 dark:hover:bg-red-500/10 cursor-pointer"
            :aria-label="`Disconnect ${appName(app)}`"
            @click="askDisconnect(app)"
          >
            Disconnect
          </button>
        </li>
      </ul>
    </div>

    <ConfirmDialog
      :open="disconnecting !== null"
      :title="`Disconnect ${disconnecting ? appName(disconnecting) : 'app'}?`"
      message="The app loses access immediately. To use it again, connect it again from the app."
      confirm-label="Disconnect"
      busy-label="Disconnecting…"
      :busy="disconnectBusy"
      :error="disconnectError"
      @confirm="confirmDisconnect"
      @cancel="disconnecting = null"
    />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { LinkIcon } from '@heroicons/vue/24/outline';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { listConnectedApps, revokeConnectedApp, type ConnectedApp } from '@/api/oauth';
import { oauthApiError } from '@/lib/oauth';
import { scopeInfo } from '@/lib/personal-access-tokens';
import { useToastStore } from '@/stores/toast';

const toast = useToastStore();

const apps = ref<ConnectedApp[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const disconnecting = ref<ConnectedApp | null>(null);
const disconnectBusy = ref(false);
const disconnectError = ref<string | null>(null);

function appName(app: ConnectedApp) {
  return app.clientName ?? 'Unnamed app';
}

function hostOf(clientId: string) {
  try {
    return new URL(clientId).host;
  } catch {
    return clientId;
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

async function loadApps() {
  loading.value = true;
  listError.value = null;
  try {
    apps.value = await listConnectedApps();
  } catch (e) {
    listError.value = oauthApiError(e, 'load connected apps').message;
  } finally {
    loading.value = false;
  }
}

function askDisconnect(app: ConnectedApp) {
  disconnectError.value = null;
  disconnecting.value = app;
}

async function confirmDisconnect() {
  const target = disconnecting.value;
  if (!target) return;
  disconnectBusy.value = true;
  disconnectError.value = null;
  try {
    await revokeConnectedApp(target.grantId);
  } catch (e) {
    const error = oauthApiError(e, 'disconnect the app');
    // Already gone, for example revoked by the app itself; the list only needs to catch up.
    if (!error.notFound) {
      disconnectError.value = error.message;
      disconnectBusy.value = false;
      return;
    }
  }
  apps.value = apps.value.filter((app) => app.grantId !== target.grantId);
  disconnecting.value = null;
  disconnectBusy.value = false;
  toast.success(`${appName(target)} was disconnected.`);
}

onMounted(loadApps);
</script>
